import React from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { appShellTestIds } from "@/testids/authTestIds";

/**
 * What the app looks like while auth resolves.
 *
 * `AuthGate` blocks on `ready`, which is Keycloak init AND the memberships
 * read — two serial round trips, and against the shared remote Keycloak that
 * is measured in seconds, not milliseconds. That whole time used to be a 48px
 * logo and the word "Loading..." centred on an empty background, so the app
 * looked like it had not started rather than like it was starting.
 *
 * This renders the real shell's geometry instead, reusing the same layout
 * classes DashboardLayout uses (`.app-shell` / `.fx-rail` / `.app-layout` /
 * `.topbar` / `.app-content`). Reusing them rather than approximating them is
 * the point: the rail is 216px and the top bar 60px in exactly one place, so
 * the skeleton cannot drift from the real thing, and the content does not jump
 * sideways when the real shell replaces it.
 *
 * Deliberately no nav labels or workspace name — only shimmer blocks. A
 * skeleton that guesses at real text is a worse lie than an empty box, and the
 * nav is role-dependent, so at this point we genuinely do not know what belongs
 * in it.
 *
 * `immediate` is set on every block: the 150ms anti-flash delay in Skeleton
 * exists for fast responses, and this wait is already known to be long.
 */
export function AppShellSkeleton(): React.ReactElement {
  return (
    <div
      className="app-shell"
      role="status"
      aria-busy="true"
      aria-label="Loading fastn"
      data-testid={appShellTestIds.loadingSkeleton}
    >
      <aside className="fx-rail hidden md:flex" aria-hidden="true">
        <div className="wsHead">
          <Skeleton immediate className="h-6 w-6 shrink-0 rounded-md" />
          <Skeleton immediate className="ms-2 h-4 w-24" />
        </div>
        <div className="flex flex-col gap-2 p-3">
          {/* Row count matches the real rail closely enough that the block of
              navigation does not visibly resize when it arrives. */}
          {Array.from({ length: 8 }, (_, i) => (
            <Skeleton immediate key={i} className="h-7 w-full" />
          ))}
        </div>
      </aside>

      <div className="app-layout">
        <div className="topbar" aria-hidden="true">
          <Skeleton immediate className="h-7 w-40" />
          <Skeleton immediate className="h-8 w-full" />
          <div className="flex items-center justify-end gap-2">
            <Skeleton immediate className="h-7 w-7 rounded-full" />
            <Skeleton immediate className="h-7 w-7 rounded-full" />
          </div>
        </div>

        <main className="app-content">
          <div className="fx-page">
            <Skeleton immediate className="h-8 w-64" />
            <Skeleton immediate className="mt-2 h-4 w-96" />
            <Skeleton immediate className="mt-6 h-48 w-full" />
          </div>
        </main>
      </div>
    </div>
  );
}
